import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserData } from "../../../infrastructure/storage/tokenStorage";
import { RiShieldKeyholeLine } from "react-icons/ri";
import { useSettings } from "../../hooks/useSettings";

const ChangePasswordPage = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [validationError, setValidationError] = useState("");

  const {
    changePassword,
    loading,
    error: apiError,
    successMessage: apiSuccess,
  } = useSettings();

  useEffect(() => {
    const data = getUserData();
    setUserData(data);
  }, []);

  const handleChangePassword = async () => {
    setValidationError("");

    if (!oldPassword || !newPassword || !confirmPassword) {
      setValidationError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError("New password and confirmation do not match");
      return;
    }

    if (newPassword.length < 6) {
      setValidationError("Password must be at least 6 characters");
      return;
    }

    const result = await changePassword(oldPassword, newPassword);

    if (result.success) {
      navigate("/settings", {
        state: {
          toastMessage: "Password changed successfully!",
          toastType: "success",
        },
        replace: true,
      });
    }
  };

  if (!userData) return null;

  const displayError = validationError || apiError;
  const displaySuccess = apiSuccess;

  return (
    <div className="min-h-screen bg-bg-main flex items-center justify-center px-4 sm:px-8 md:px-[10%] lg:px-[15%] py-8 sm:py-12">
      <div className="w-full max-w-[520px]">
        <div className="bg-primary-dark rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 lg:p-12">
          <div className="text-center mb-6 sm:mb-8 md:mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-primary-cyan mb-3 sm:mb-4 bg-primary-cyan/10">
              <RiShieldKeyholeLine className="text-primary-cyan text-2xl sm:text-3xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-3 tracking-tight">
              Change Password
            </h2>
            <p className="text-gray-300 text-sm sm:text-base tracking-wide break-words">
              Update your password for{" "}
              <span className="text-primary-cyan font-semibold break-all">
                {userData.email}
              </span>
            </p>
          </div>

          {displayError && (
            <div className="bg-red-500/20 border border-red-500 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
              <p className="text-red-400 text-xs sm:text-sm text-center font-medium">
                {displayError}
              </p>
            </div>
          )}

          {displaySuccess && !validationError && (
            <div className="bg-green-500/20 border border-green-500 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
              <p className="text-green-400 text-xs sm:text-sm text-center font-medium">
                {displaySuccess}
              </p>
            </div>
          )}

          <label className="block text-gray-200 text-xs font-bold uppercase tracking-wider mb-2">
            Current Password
          </label>
          <input
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="w-full bg-primary-dark border border-gray-600 rounded-xl px-4 sm:px-5 py-3 text-white text-sm sm:text-base mb-5 sm:mb-6 focus:border-primary-cyan focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 transition-all duration-200"
            placeholder="Enter your current password"
            type="password"
            disabled={loading}
          />

          <label className="block text-gray-200 text-xs font-bold uppercase tracking-wider mb-2">
            New Password
          </label>
          <input
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full bg-primary-dark border border-gray-600 rounded-xl px-4 sm:px-5 py-3 text-white text-sm sm:text-base mb-5 sm:mb-6 focus:border-primary-cyan focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 transition-all duration-200"
            placeholder="Enter new password"
            type="password"
            disabled={loading}
          />

          <label className="block text-gray-200 text-xs font-bold uppercase tracking-wider mb-2">
            Confirm New Password
          </label>
          <input
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full bg-primary-dark border border-gray-600 rounded-xl px-4 sm:px-5 py-3 text-white text-sm sm:text-base mb-6 sm:mb-8 focus:border-primary-cyan focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 transition-all duration-200"
            placeholder="Confirm your new password"
            type="password"
            disabled={loading}
          />

          <button
            onClick={handleChangePassword}
            disabled={loading}
            className="w-full bg-primary-cyan text-white font-semibold py-3 sm:py-4 rounded-xl text-sm sm:text-base hover:opacity-90 hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Updating...
              </span>
            ) : (
              "Update Password"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;